import { Button, Divider, Spin } from "antd";
import { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import ContentLayoutManagement from "../../../../layouts/ContentLayoutManagement";
import HeaderContent from "../../../../layouts/HeaderContent";
import CardItemInCart from "../../../../components/VMRS/Cart/CardItemInCart";
import UnifiedDynamicForm from "../../../../components/VMRS/ExploreTasking/components/form/UnifiedDynamicForm";
import CommonComponents from "../../../../components/Commons";
import useToggle from "../../../../hooks/useToggle";

import { RootState } from "../../../../store/store";
import { getGeneralFormOrderArchiveAsync, createArchiveOrderV2Async } from "../../../../store/reducers/orderSlice";
import
{
  getImgsAsync,
  setArrIdImage,
  setSelectedImages,
  setSelectedImagesConfirm,
  setSelectedItems,
} from "../../../../store/reducers/cartSlice";
import { showError, showSuccess } from "../../../../helpers/toast";
import { getErrorMsg } from "../../../../helpers/common";
import { ESttausCode } from "../../../../enums";
import { UniversalMap } from "../../../../components/VMRS/MapAdapter/UniversalMap";
import NoData from "../../../../components/NoData";
import { clearAllLayers } from "../../../../components/VMRS/MapAdapter/store/slices/layerSlice";
import { clearAOIFeatures, setDrawingAOIMode } from "../../../../components/VMRS/MapAdapter/store/slices/aoiSlice";
import { setDrawingMode } from "../../../../components/VMRS/MapAdapter/store/slices/drawSlice";
import { setMeasureMode } from "../../../../components/VMRS/MapAdapter/store/slices/measureSlice";

const transformGeneralSchema = (apiData: any) =>
{
  return [
    {
      step_id: "general_step",
      name: apiData?.name,
      ui: apiData?.ui_schema?.ui,
    },
  ];
};
const CreateArchiveOrder = () =>
{
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const selectedItems = useSelector(
    (state: RootState) => state.cartSlice.selectedItems,
  );

  const [loading, setLoading] = useState(false);
  const [loadingSchemas, setLoadingSchemas] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const [openProviders, setOpenProviders] = useState<Record<string, boolean>>(
    {},
  );
  const [engine, setEngine] = useState<any>(null);

  const [generalSteps, setGeneralSteps] = useState<any[]>([]);

  const {
    open: openConfirm,
    toggle: toggleConfirm,
    shouldRender: shouldRenderConfirm,
  } = useToggle();


  // =============================
  // GROUP ITEMS BY PROVIDER
  // =============================
  const itemsByProvider = useMemo(() =>
  {
    const groups: Record<string, any[]> = {};
    selectedItems?.forEach((item: any) =>
    {
      const key = item.provider_code?.toLowerCase() || "unknown";
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });
    return groups;
  }, [selectedItems]);

  // =============================
  // RESET MAP STATE ON MOUNT/UNMOUNT
  // (so layers from previous page like /cart don't leak in)
  // =============================
  const handleResetMap = () =>
  {
    dispatch(clearAOIFeatures());
    dispatch(clearAllLayers());
    dispatch(setDrawingAOIMode("NONE"));
    dispatch(setDrawingMode("NONE"));
    dispatch(setMeasureMode("NONE"));
  };

  useEffect(() =>
  {
    handleResetMap();
    return () =>
    {
      handleResetMap();
    };
  }, []);

  // =============================
  // FETCH GENERAL SCHEMA
  // =============================
  useEffect(() =>
  {
    setLoadingSchemas(true);
    (dispatch(getGeneralFormOrderArchiveAsync()) as any)
      .unwrap()
      .then((res: any) =>
      {
        const data = res?.data?.data;
        setGeneralSteps(transformGeneralSchema(data));
      })
      .catch(() => { })
      .finally(() => setLoadingSchemas(false));
  }, []);

  // =============================
  // INIT DEFAULT FORM DATA
  // =============================
  useEffect(() =>
  {
    const defaults: any = {};

    generalSteps.forEach((step: any) =>
    {
      step.ui?.order?.forEach((field: string) =>
      {
        const config = step.ui.options[field];
        if (config?.default !== undefined)
        {
          defaults[field] = config.default;
        }
      });
    });

    setFormData(defaults);
  }, [generalSteps]);

  // =============================
  // INIT COLLAPSE STATE
  // =============================
  useEffect(() =>
  {
    const init: any = {};
    Object.keys(itemsByProvider).forEach((k) =>
    {
      init[k] = true;
    });
    setOpenProviders(init);
  }, [itemsByProvider]);

  const toggleProvider = (key: string) =>
  {
    setOpenProviders((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // =============================
  // VALIDATE
  // =============================
  const validate = () =>
  {
    const errors: string[] = [];

    const isEmpty = (val: any) =>
      val === undefined || val === null || val === "";

    const getRequiredFields = (step: any) =>
      Object.entries(step.ui?.options || {})
        .filter(([, cfg]: [string, any]) => cfg.required === true)
        .map(([key]) => key);

    for (const step of generalSteps)
    {
      for (const field of getRequiredFields(step))
      {
        if (isEmpty(formData[field]))
        {
          errors.push(field);
        }
      }
    }

    if (errors.length > 0)
    {
      showError(t("Missing required fields") + ": " + errors.join(", "));
      return false;
    }

    return true;
  };

  // =============================
  // SUBMIT
  // =============================
  const handleSubmit = () =>
  {
    if (!validate()) return;
    toggleConfirm();
  };

  const handleConfirmSubmit = async () =>
  {
    try
    {
      toggleConfirm();
      setLoading(true);

      const payload: any = {
        ...formData,
        order_type: "archive",
      };

      Object.entries(itemsByProvider).forEach(([providerKey, items]) =>
      {
        payload[providerKey] = {
          item_ids: items.map((i: any) => i.id),
        };
      });

      const res = await dispatch(createArchiveOrderV2Async(payload)).unwrap();


      if (
        res?.status === ESttausCode.OK ||
        res?.status === ESttausCode.CREATED
      )
      {
        showSuccess(t("Create archive order successfully"));

        // Refresh cart from BE so the sidebar total drops after items moved to order
        try
        {
          const cartRes = await dispatch(getImgsAsync()).unwrap();
          if (cartRes?.status === ESttausCode.OK)
          {
            dispatch(
              setArrIdImage(
                cartRes?.data?.data?.items?.map((it: any) => it?.stac_item_id) ?? [],
              ),
            );
          }
        } catch { }
        // Also clear the local selection so cart page resets
        dispatch(setSelectedItems([]));
        dispatch(setSelectedImages([]));
        dispatch(setSelectedImagesConfirm([]));

        navigate(`/order/management-order/${res?.data?.data?.id}`);
      } else
      {
        showError(t(res?.data?.message || res?.message) || t("Create archive order failed"));
      }
    } catch (err: any)
    {
      showError(t(err?.message || getErrorMsg(err)));
    } finally
    {
      setLoading(false);
    }
  };

  // =============================
  // UI
  // =============================
  return (
    <ContentLayoutManagement
      header={
        <HeaderContent
          breadcrumb={[{ title: t("Cart") }, { title: t("Create Archive Order") }]}
        />
      }
    >
      <Spin spinning={loading || loadingSchemas}>
        <div className="w-full h-full flex bg-[var(--neutral-1000)] text-white">
          {/* LEFT PANEL */}
          <div className="w-5/12 h-full bg-[#0F172A] text-white flex flex-col border-r border-gray-800">
            {selectedItems?.length > 0 ? (
              <>
                <div className="overflow-auto h-[calc(100%-65px)] pb-6">
                  {/* ===== GENERAL ===== */}
                  <div className="space-y-4 pt-6">
                    <p className="uppercase text-sm px-6">
                      {t("General Information")}
                    </p>

                    {generalSteps.map((step: any, idx: number) => (
                      <UnifiedDynamicForm
                        key={idx}
                        step={{ ui: step.ui }}
                        formData={formData}
                        setFormData={setFormData}
                        display="grid"
                      />
                    ))}
                  </div>

                  <Divider />

                  {/* ===== PROVIDERS (items only, no form) ===== */}
                  {Object.entries(itemsByProvider).map(
                    ([providerKey, items], index) => (
                      <div key={providerKey} className="space-y-4">
                        {/* HEADER */}
                        <div
                          className="flex justify-between items-center cursor-pointer px-6"
                          onClick={() => toggleProvider(providerKey)}
                        >
                          <p className="uppercase text-sm">
                            {providerKey} ({items.length})
                          </p>

                          <span>{openProviders[providerKey] ? "▲" : "▼"}</span>
                        </div>

                        {/* CARD LIST */}
                        {openProviders[providerKey] && (
                          <div className="space-y-2 px-6">
                            {items.map((item: any) => (
                              <CardItemInCart
                                key={item.id}
                                item={item}
                                mapInstance={engine?.getMapInstance?.() ?? engine}
                                showCheckBox={false}
                              />
                            ))}
                          </div>
                        )}

                        {index <
                          Object.entries(itemsByProvider)?.length - 1 && (
                            <div className="px-6">
                              <Divider />
                            </div>
                          )}
                      </div>
                    ),
                  )}
                </div>

                {/* FOOTER */}
                <div className="p-4 border-t border-gray-800 flex justify-between w-full">
                  <Button className="btn-outline" onClick={() => navigate(-1)}>
                    {t("Back")}
                  </Button>
                  <Button
                    className="w-[85px] !border-none !bg-[var(--primary-4)] text-[var(--primary-950)]"
                    type="primary"
                    onClick={handleSubmit}
                    loading={loading}
                  >
                    {t("Submit")}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <NoData
                  extraButton={
                    <Button
                      onClick={() => navigate("/cart")}
                      className="w-[125px] !border-none !bg-[var(--primary-4)] text-[var(--primary-950)] mt-2"
                    >
                      {t("Back to cart")}
                    </Button>
                  }
                />
              </>
            )}
          </div>

          {/* RIGHT MAP */}
          <div className="flex-1">
            <UniversalMap
              mapType="maplibre"
              showEngine={false}
              showDraw={false}
              showDrawAOI={false}
              showMeasure={false}
              view={{
                center: [108.23653393802581, 16.1039225743402],
                zoom: 5,
              }}
              showBaseMap={true}
              showMapControl={true}
              onMapReady={setEngine}
            />
          </div>
        </div>
      </Spin>

      {shouldRenderConfirm && (
        <CommonComponents.ModalUI
          title={t("Confirm order")}
          open={openConfirm}
          toggle={toggleConfirm}
          handleConfirm={handleConfirmSubmit}
          loading={loading}
        >
          <p>{t("Are you sure you want to create this archive order?")}</p>
        </CommonComponents.ModalUI>
      )}
    </ContentLayoutManagement>
  );
};

export default CreateArchiveOrder;
